import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function WirralCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Wirral Pothole Claims | 79% U-Roads, 25% Green | Fixtyer"
        description="Wirral Council earns GREEN on spend and best practice yet AMBER overall — 79% of its 1,182km network is U-roads with only 25% in GREEN condition, and C-road good condition has fallen 12 points since 2020. See what that means for your pothole claim."
        canonical="/councils/wirral"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Wirral', url: '/councils/wirral' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Wirral has GREEN spend and best-practice scorecards — can I still claim?',
            answer: 'Yes. Wirral\'s DfT Spend and Best Practice ratings are GREEN, but the overall rating is AMBER because of road condition. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spend or asset-management paperwork. Wirral\'s own survey data shows C-road GREEN condition down from 37.5% to 25.4% since 2020, and only 25.2% of U-roads in GREEN at the latest survey.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 932.7km — 79% of Wirral\'s 1,181.9km carriageway network. At the 2024 survey, 12.5% of U-roads were in RED condition, 62.3% in amber, and just 25.2% in green. U-road condition is recorded annually when the classified network is surveyed. The council\'s 2025 planned treatment covers 79.98km of U-roads — roughly 8.6% of the residential network in a single year.',
          },
          {
            question: 'Does Wirral\'s pothole definition affect my claim?',
            answer: 'Wirral defines a pothole as a break in the carriageway surface of more than 250mm x 300mm x 40mm in depth with sharp edges. Smaller defects may not be classified or repaired as potholes under the council\'s own threshold — yet they can still damage vehicles and still fall within Section 41 of the Highways Act 1980 if the road is not reasonably maintained. Your claim should focus on the actual defect dimensions and whether it met the council\'s intervention criteria.',
          },
          {
            question: 'Does Wirral\'s admission of deteriorating assets help my claim?',
            answer: 'Yes. Wirral\'s transparency report states the council is in "an increasingly challenging environment, with deteriorating assets, increasing traffic volumes and uncertainty around future funding." That is documented knowledge that the network is worsening — which raises the bar for what a reasonable inspection and maintenance regime should achieve on your specific road.',
          },
          {
            question: 'Wirral filled only 657 potholes in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. Wirral filled 657 potholes in 2024/25, down from 1,058 in 2023/24, but 3,382 over five years. The council attributes lower numbers partly to preventative surface treatments. Yet U-road GREEN condition fell from 34.1% in 2020 to 25.2% in 2024, and C-road GREEN condition fell from 37.5% to 25.4% in the same period. Fewer counted potholes and worsening survey data can coexist — especially where the council\'s definition excludes smaller defects.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Wirral Council"
        councilSlug="wirral"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/wirral"
        headline="Wirral Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Wirral</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Wirral: Only 25% of Residential Roads in GREEN
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Wirral earns <strong className="text-emerald-400">GREEN</strong> scorecards for spend and best practice,
            yet the overall DfT rating is <strong className="text-amber-400">AMBER</strong> — because the roads
            themselves are slipping. <strong className="text-rose-400">79% of the network is U-roads</strong>, and
            at the latest survey only <strong className="text-rose-400">25.2% were in GREEN condition</strong> while
            62.3% sat in amber. C-road good condition has fallen 12 percentage points since 2020.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">62.3%</div>
            <div className="text-white font-medium mt-2">U-roads in amber condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Maintenance may be required soon — on 581km of Wirral's 933km residential network.
              Only 25.2% of U-roads were in GREEN at the same survey.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of independent survey data from Wirral's own transparency report — classified roads
            surveyed annually, U-roads surveyed alongside them
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (131.1km — 11% of network): slipping</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">3.7%</td>
                    <td className="px-4 py-2 text-center">45.7%</td>
                    <td className="px-4 py-2 text-center">50.6%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">4.43%</td>
                    <td className="px-4 py-2 text-center">49.88%</td>
                    <td className="px-4 py-2 text-center">45.64%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">3.93%</td>
                    <td className="px-4 py-2 text-center">46.5%</td>
                    <td className="px-4 py-2 text-center">46.02%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">4.07%</td>
                    <td className="px-4 py-2 text-center">48.86%</td>
                    <td className="px-4 py-2 text-center">38.26%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">4.78%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">53.32%</td>
                    <td className="px-4 py-2 text-center font-bold">41.9%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              GREEN A-roads fell from 50.6% to 41.9% over five years. More than half the A-road network
              now sits in amber — maintenance may be required soon.
            </p>
          </div>

          {/* B roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B roads: RED up, GREEN down</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">3.21%</td>
                    <td className="px-4 py-2 text-center">50.54%</td>
                    <td className="px-4 py-2 text-center">46.25%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">5.66%</td>
                    <td className="px-4 py-2 text-center">56.47%</td>
                    <td className="px-4 py-2 text-center">37.61%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">5.54%</td>
                    <td className="px-4 py-2 text-center">54.6%</td>
                    <td className="px-4 py-2 text-center">39.86%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">6.0%</td>
                    <td className="px-4 py-2 text-center">51.95%</td>
                    <td className="px-4 py-2 text-center">42.05%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">5.06%</td>
                    <td className="px-4 py-2 text-center font-bold">52.0%</td>
                    <td className="px-4 py-2 text-center font-bold">42.95%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B-road RED condition nearly doubled from 3.21% in 2020 to a peak of 6.0% in 2023.
              Over half the B-road network has been in amber every year since 2021.
            </p>
          </div>

          {/* C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">C roads: the steepest decline</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">7.1%</td>
                    <td className="px-4 py-2 text-center">55.4%</td>
                    <td className="px-4 py-2 text-center">37.53%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">9.74%</td>
                    <td className="px-4 py-2 text-center">59.68%</td>
                    <td className="px-4 py-2 text-center">30.46%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">10.9%</td>
                    <td className="px-4 py-2 text-center">61.22%</td>
                    <td className="px-4 py-2 text-center">27.45%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">9.5%</td>
                    <td className="px-4 py-2 text-center">63.44%</td>
                    <td className="px-4 py-2 text-center">26.93%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">10.4%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">64.26%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">25.36%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              C-road GREEN condition has fallen from 37.53% to 25.36% — a 12-point drop. Nearly two-thirds
              of C-roads are now in amber, and more than one in ten is in RED. B and C roads combined
              total 118.1km across the borough.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And This Is The Well-Funded Version</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£6.9m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£6.9m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">65%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Wirral matches its DfT allocation pound for pound, targets 65% preventative maintenance,
              and earns <span className="text-emerald-400">GREEN on both spend and best practice</span> — yet
              the overall rating is AMBER because condition keeps slipping. The problem is not the chequebook.
              The roads are deteriorating faster than treatment can hold them.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road majority */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 933km Residential Majority
          </h2>
          <p className="text-surface-600 mb-8">
            79% of Wirral's carriageway network is U-roads — and most of it is not in good condition
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-rose-700">Red</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-amber-700">Amber</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-emerald-700">Green</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">12.5%</td>
                  <td className="px-6 py-4 text-center">53.43%</td>
                  <td className="px-6 py-4 text-center">34.07%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13.79%</td>
                  <td className="px-6 py-4 text-center">58.47%</td>
                  <td className="px-6 py-4 text-center">26.68%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14.11%</td>
                  <td className="px-6 py-4 text-center">56.28%</td>
                  <td className="px-6 py-4 text-center">26.06%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13.57%</td>
                  <td className="px-6 py-4 text-center">58.17%</td>
                  <td className="px-6 py-4 text-center">24.71%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">12.5%</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">62.28%</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">25.22%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Annual U-Road Surveys</h3>
              <p className="text-surface-600 text-sm mb-3">
                Wirral records U-road condition data annually when the classified road network is surveyed.
                The council states it achieves 100% of inspections on time — but an annual survey cycle
                still means a defect can form and worsen between passes.
              </p>
              <p className="text-surface-600 text-sm">
                At the 2024 survey, <strong>117km of U-roads were in RED condition</strong> (12.5% of 932.7km)
                and <strong>581km sat in amber</strong> — roads where maintenance may be required soon.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Treatment vs Network Size</h3>
              <p className="text-amber-800 text-sm mb-3">
                Wirral's 2025 planned carriageway treatment covers 79.98km of U-roads — against a
                932.7km network. That is roughly 8.6% of the residential road estate in a single year,
                even as GREEN U-road condition has fallen from 34.1% to 25.2% since 2020.
              </p>
              <p className="text-amber-800 text-sm">
                The council's own strategy aims to increase preventative treatments on unclassified roads
                to slow deterioration — but survey data shows deterioration continuing regardless.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for
              knowing the condition of its roads. For Wirral's U-road network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your U-road last condition-surveyed — and what did the data show?</li>
              <li>• If 62.3% of U-roads were in amber at the last survey, what was done about yours?</li>
              <li>• Did the defect meet Wirral's own intervention criteria before you hit it?</li>
              <li>• Was the road selected from survey data that was months old when the programme was published?</li>
            </ul>
            <p className="text-white font-medium">
              A council that admits deteriorating assets cannot claim its roads are in reasonable repair
              on every street — only that it has systems in place. Your claim tests whether those systems
              worked on your specific road.
            </p>
          </div>
        </div>
      </section>

      {/* Potholes */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            3,382 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Wirral's own pothole counts — and the definition that shapes what gets counted
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Reactive spend on potholes</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020 to 2021</td>
                  <td className="px-6 py-4 text-center">296</td>
                  <td className="px-6 py-4 text-center">2.4%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021 to 2022</td>
                  <td className="px-6 py-4 text-center">617</td>
                  <td className="px-6 py-4 text-center">5.0%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022 to 2023</td>
                  <td className="px-6 py-4 text-center">754</td>
                  <td className="px-6 py-4 text-center">6.3%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023 to 2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">1,058</td>
                  <td className="px-6 py-4 text-center">8.8%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024 to 2025</td>
                  <td className="px-6 py-4 text-center">657</td>
                  <td className="px-6 py-4 text-center">4.1%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">3,382</td>
                  <td className="px-6 py-4 text-center">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Wirral defines a pothole as the breaking open of a carriageway surface, by more than 250mm x 300mm x 40 mm in depth, with sharp edges. This is the minimum size, but they can range up to 1000mm x 1000mm, anything more than this in size will be defined as a road defect."
              source="Wirral Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Size Threshold</h3>
              <p className="text-surface-600 text-sm">
                A defect smaller than 250mm x 300mm x 40mm may not be classified as a pothole under
                Wirral's definition — yet it can still damage a tyre or suspension. Your claim should
                document the actual dimensions and argue the road was not reasonably maintained under
                Section 41, regardless of the council's label.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Lower Counts, Worse Surveys</h3>
              <p className="text-amber-800 text-sm">
                Pothole numbers fell from 1,058 to 657 between 2023/24 and 2024/25 — yet U-road GREEN
                condition remained at just 25.2% (down from 34.1% in 2020) and C-road GREEN fell from
                26.9% to 25.4% over the same period. Preventative treatments may reduce reactive counts without fixing the
                underlying deterioration the surveys record.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Council admissions */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What Wirral Says In Its Own Words
          </h2>
          <p className="text-surface-600 mb-8">
            Admissions from the council's transparency report and road maintenance pages
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Despite making substantial progress in recent years, we recognise we are in an increasingly challenging environment, with deteriorating assets, increasing traffic volumes and uncertainty around future funding."
              source="Wirral Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="As a result, we have been able to maximise and secure additional funding and, continue successfully defending claims."
              source="Wirral Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Wirral's strategic plan over the past 5 years is to increase spending on preventative treatments, while holding steady rate of network deterioration and keeping reactive repairs to a minimum."
              source="Wirral Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Deteriorating Assets, Defended Claims</h3>
              <p className="text-surface-600 text-sm mb-3">
                Wirral states it has been able to "continue successfully defending claims" while
                simultaneously acknowledging deteriorating assets. That tells you two things: the
                council has a polished Section 58 defence, and it knows the network is worsening.
              </p>
              <p className="text-surface-600 text-sm">
                Generic claims against a council that actively defends will struggle. Specificity —
                prior reports, photos, road class, inspection frequency — is how you beat a prepared defence.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Holding Steady — On Paper</h3>
              <p className="text-amber-800 text-sm mb-3">
                Wirral's stated strategy is to hold the rate of network deterioration steady while
                keeping reactive repairs to a minimum. Yet C-road GREEN condition fell from 37.53%
                to 25.36% between 2020 and 2024, and U-road GREEN fell from 34.07% to 25.22%.
              </p>
              <p className="text-amber-800 text-sm">
                The gap between strategic intent and survey outcomes is exactly what your claim
                should press — especially on residential roads where amber now exceeds 62%.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inspection methodology */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            How Wirral Surveys Its Roads
          </h2>
          <p className="text-surface-600 mb-8">
            Independent driven inspections with 360-degree HD imagery — and a methodology change coming
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Wirral commission an independent consultant to carry out a driven inspection to survey Wirral's roads and footways. This survey produces 360-degree high-definition carriageway imagery that is used to determine the road condition and assist with producing future programmes of surfacing work."
              source="Wirral Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">RED, Amber, Green Definitions</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>Green</strong> — no further investigation or treatment required</li>
                <li>• <strong>Amber</strong> — maintenance may be required soon</li>
                <li>• <strong>Red</strong> — should be considered for maintenance; the road should be investigated fully</li>
              </ul>
              <p className="text-surface-600 text-sm mt-3">
                A road in amber is not "fine" — the council's own survey classifies it as needing
                maintenance soon. That is documentary evidence the network knew trouble was coming.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">PAS 2161 From 2026/27</h3>
              <p className="text-amber-800 text-sm">
                From 2026/27 Wirral will switch to the BSI PAS 2161 standard, categorising roads into
                five condition bands instead of three. If your incident falls either side of that change,
                condition records before and after may not be directly comparable — something to flag
                if the council cites improving post-2026 data.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Concrete Roads and Thermal Patching</h3>
            <p className="text-brand-200 mb-4">
              Wirral has a high number of concrete roads given thin overlays. The council is removing
              overlays, restoring concrete and sealing joints through "concrete rejuvenation" — because
              potholes do not form on properly sealed concrete surfaces. It has also introduced thermal
              patching focused on junction areas to remove future reactive patching.
            </p>
            <p className="text-white font-medium">
              If your incident was on a concrete road or at a junction, check whether rejuvenation or
              thermal patching had reached that location — or whether the council was still managing
              deterioration reactively.
            </p>
          </div>
        </div>
      </section>

      {/* Claims balanced section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Run AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Wirral is organised about its highways — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — matches DfT capital allocation in 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN best practice — HIAMP, Well-Managed Highway Infrastructure code
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 65% preventative maintenance targeted, up from 45% in 2020/21
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Claims 100% of inspections and emergency works attended on time
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Actively defends claims — expects a documented Section 58 response
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a prepared defence. Generic template letters will not cut it.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — C-road GREEN down 12 points since 2020
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 79% of network is U-roads — only 25.2% in GREEN at latest survey
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 62.3% of U-roads in amber at the latest survey
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits deteriorating assets and roads that worsen faster than expected
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Pothole definition excludes defects under 250mm x 300mm x 40mm
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits deteriorating assets despite GREEN spend scorecards
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and best-practice scorecards that actively defends claims,
              your claim lives or dies on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing defect size against Wirral's 250mm x 300mm x 40mm threshold</li>
              <li>• Road class — on a U-road in amber condition, the survey data itself supports your argument</li>
              <li>• Whether the road was on the annual maintenance programme or had deteriorated beyond survey data</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence,
              and cites Wirral's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Wirral?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-run council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road condition data cited</li>
                <li>• No prior-report search</li>
                <li>• No pothole-threshold argument</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ C-road decline and U-road amber majority documented</li>
                <li>✅ U-road amber majority and survey gap argued</li>
                <li>✅ Wirral's pothole definition and threshold cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Wirral</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=wirral"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Wirral Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Wirral has GREEN spend and best-practice scorecards — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Wirral's DfT Spend and Best Practice ratings are GREEN, but the overall rating is AMBER
                because of road condition. Section 58 turns on whether the specific defect that damaged your
                vehicle was reasonably inspected and repaired — not on aggregate spend or asset-management
                paperwork. Wirral's own survey data shows C-road GREEN condition down from 37.5% to 25.4%
                since 2020, and only 25.2% of U-roads in GREEN at the latest survey.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 932.7km — 79% of Wirral's 1,181.9km carriageway network. At the 2024 survey,
                12.5% of U-roads were in RED condition, 62.3% in amber, and just 25.2% in green. U-road
                condition is recorded annually when the classified network is surveyed. The council's 2025 planned treatment covers
                79.98km of U-roads — roughly 8.6% of the residential network in a single year.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Wirral's pothole definition affect my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Wirral defines a pothole as a break in the carriageway surface of more than 250mm x 300mm x 40mm
                in depth with sharp edges. Smaller defects may not be classified or repaired as potholes under
                the council's own threshold — yet they can still damage vehicles and still fall within Section 41
                of the Highways Act 1980 if the road is not reasonably maintained. Your claim should focus on
                the actual defect dimensions and whether it met the council's intervention criteria.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Wirral's admission of deteriorating assets help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Wirral's transparency report states the council is in "an increasingly challenging environment,
                with deteriorating assets, increasing traffic volumes and uncertainty around future funding." That
                is documented knowledge that the network is worsening — which raises the bar for what a reasonable
                inspection and maintenance regime should achieve on your specific road.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Wirral filled only 657 potholes in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Wirral filled 657 potholes in 2024/25, down from 1,058 in 2023/24, but 3,382 over five years.
                The council attributes lower numbers partly to preventative surface treatments. Yet U-road GREEN
                condition fell from 34.1% in 2020 to 25.2% in 2024, and C-road GREEN fell from 37.5% to 25.4%
                over the same period. Fewer counted potholes and worsening survey data can coexist —
                especially where the council's definition excludes smaller defects.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.wirral.gov.uk/parking-roads-and-travel/roads-and-pavements/road-maintenance/highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Wirral Council Local Highways Maintenance Transparency Report 2025
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
